import { Form, Row } from 'antd';
import dayjs from 'dayjs';
import { sum } from 'lodash';
import React, { useEffect, useState } from 'react';
import { IAmlz, ISubj, Ic3prima } from 'types/amlz';

import AnchorComponent from 'components/atoms/AnchorComponent';
import ButtonComponent from 'components/atoms/ButtonComponent';
import Paragraph from 'components/atoms/Paragraph';
import SelectComponent from 'components/atoms/SelectComponent';
import TableComponent from 'components/atoms/TableComponent';
import Title from 'components/atoms/Title';
import TranslateText from 'components/hoc/TranslateText';
import FormWrapper from 'components/molecules/FormWrapper';
import InfoTooltip from 'components/molecules/InfoTooltip';

import { IcisStaty } from 'services/cisStaty';

import { EAnoNe } from 'enums/anoNe';

import anoNeSelect from 'data/codebooks/anoNeSelect';

import C3PridatOsobu from './C3PridatOsobu';

interface IC3 {
    C3dataPrima?: Ic3prima;
    countries?: IcisStaty[];
    prevStep: () => void;
    nextStep: () => void;
    SubmitFunc: <T extends keyof IAmlz['amlz']>(key: T, values: IAmlz['amlz'][T]) => void;
    setC3lastStep: (value: boolean) => void;
    isC6edit: string;
    setSaving: React.Dispatch<React.SetStateAction<boolean>>;
    isLegalEntity: boolean;
}

const C3: React.FC<IC3> = ({
    C3dataPrima,
    countries,
    prevStep,
    SubmitFunc,
    setC3lastStep,
    isC6edit,
    nextStep,
    setSaving,
    isLegalEntity
}) => {
    const [form] = Form.useForm();
    const [personData, setPersonData] = useState<ISubj | undefined>();
    const [submitPersonData, setSubmitPersonData] = useState<ISubj | null>();
    const [subjIndex, setSubjIndex] = useState<number>(-1);
    const [deletePerson, setDeletePerson] = useState<number>(-1);
    const [showTable, setShowTable] = useState<EAnoNe | ''>(C3dataPrima?.ma ? C3dataPrima.ma : '');
    const [percentaVlastnictva, setPercentaVlastnictva] = useState<number>(0);

    const handleSelectChange = (name: string, value: EAnoNe | '') => {
        form.setFieldsValue({
            [name]: value
        });
        setShowTable(value);
    };

    const onFinish = () => {
        SubmitFunc('c3prima', {
            ma: showTable ? showTable : EAnoNe.ANO,
            subj: showTable === EAnoNe.ANO ? C3dataPrima?.subj : []
        });
        setC3lastStep(true);
        isC6edit && nextStep();
    };
    // sucet % vlastnictva
    useEffect(() => {
        const podil = C3dataPrima?.subj?.map(e => parseFloat(e.podil ? e.podil : '0'));
        setPercentaVlastnictva(sum(podil));
        if (C3dataPrima?.ma) {
            setShowTable(C3dataPrima.ma);
            form.setFieldsValue({
                ma: C3dataPrima.ma
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [C3dataPrima]);
    // submit c3 subj
    useEffect(() => {
        if (submitPersonData) {
            const subj =
                C3dataPrima?.subj && C3dataPrima.subj.length > 0 ? JSON.parse(JSON.stringify(C3dataPrima?.subj)) : [];

            const subjToUpdate = [...subj];
            if (subjIndex > -1) {
                subjToUpdate[subjIndex] = submitPersonData;
            } else {
                subjToUpdate.push(submitPersonData);
            }
            setSaving(true);
            SubmitFunc('c3prima', {
                ma: showTable ? showTable : EAnoNe.ANO,
                subj: subjToUpdate
            });

            setSubjIndex(-1);
            setPersonData(undefined);
            setSubmitPersonData(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [submitPersonData]);

    useEffect(() => {
        if (deletePerson > -1) {
            const subj = JSON.parse(JSON.stringify(C3dataPrima?.subj));
            subj.splice(deletePerson, 1);
            setSaving(true);
            SubmitFunc('c3prima', {
                ma: showTable ? showTable : EAnoNe.ANO,
                subj: subj
            });
            setDeletePerson(-1);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [deletePerson]);

    const columns = [
        {
            title: <TranslateText>JMENO</TranslateText>,
            dataIndex: 'jmeno',
            key: 'jmeno',
            render: (_text: ISubj, row: ISubj) => (
                <p
                    style={{
                        margin: 0
                    }}
                >
                    {row.jmeno + ' ' + (row.prijmeni ? row.prijmeni : '')}
                </p>
            )
        },
        {
            title: (
                <>
                    <TranslateText>FORMULARE_RODNE_CISLO</TranslateText> /{' '}
                    <TranslateText>FORMULARE_DATUM_NAROZENI</TranslateText> / <TranslateText>ICO_ZKRATKA</TranslateText>
                </>
            ),
            dataIndex: 'idmb',
            key: 'idmb',
            render: (text: string, row: ISubj) => {
                return (
                    <p
                        style={{
                            margin: 0
                        }}
                    >
                        {text ? text : dayjs(row.datumnar).format('DD.MM.YYYY')}
                    </p>
                );
            }
        },
        {
            title: (
                <>
                    <TranslateText>FORMULARE_ZEME_PUVODU</TranslateText> /{' '}
                    <TranslateText>FORMULARE_ZEME_SIDLA</TranslateText>
                </>
            ),
            dataIndex: 'zemepuv',
            key: 'zemepuv',
            render: (_text: ISubj, row: ISubj) => {
                return (
                    <p
                        style={{
                            margin: 0
                        }}
                    >
                        {row.zemepuv?.map((e, i) => (
                            <span
                                key={i}
                                style={{
                                    paddingRight: 6,
                                    display: 'inline'
                                }}
                            >
                                {e}
                            </span>
                        ))}
                    </p>
                );
            }
        },
        {
            title: <TranslateText>FORMULARE_PODIL</TranslateText>,
            dataIndex: 'podil',
            key: 'podil',
            render: (_text: ISubj, row: ISubj) => {
                return (
                    <p
                        style={{
                            margin: 0
                        }}
                    >
                        {row.podil + '%'}
                    </p>
                );
            }
        },
        {
            width: 100,
            render: (_text: ISubj, _row: ISubj, index: number) => (
                <AnchorComponent
                    id={`C3UpravitBtn${index}`}
                    key={index}
                    onClick={() => {
                        setPersonData(C3dataPrima?.subj ? C3dataPrima.subj[index] : undefined);
                        setSubjIndex(index);
                    }}
                >
                    <TranslateText>BUTTON_UPRAVIT</TranslateText>
                </AnchorComponent>
            )
        },
        {
            width: 100,
            render: (_text: ISubj, _row: ISubj, index: number) => (
                <AnchorComponent id={`C3OdebratBtn${index}`} key={index} onClick={() => setDeletePerson(index)}>
                    <TranslateText>ODEBRAT</TranslateText>
                </AnchorComponent>
            )
        }
    ];
    return !personData ? (
        <FormWrapper onFinish={onFinish} name="C3prima" outForm={form}>
            <Title
                text={isLegalEntity ? 'FORMULARE_AMLZ_C3A_TITLE_LEGALENTITY' : 'FORMULARE_AMLZ_C3A_TITLE'}
                level={4}
            />
            <p>
                <TranslateText>
                    {isLegalEntity ? 'FORMULARE_AMLZ_C3A_PODILNIKTEXT_LEGALENTITY' : 'FORMULARE_AMLZ_C3A_PODILNIKTEXT'}
                </TranslateText>
                <InfoTooltip
                    title={isLegalEntity ? 'FORMULARE_AMLZ_C3A_TOOLTIP_LEGALENTITY' : 'FORMULARE_AMLZ_C3A_TOOLTIP'}
                />
            </p>
            <SelectComponent
                isFormItem={true}
                name="ma"
                labelParagraph={isLegalEntity ? 'FORMULARE_AMLZ_C3A_SELECT_LEGALENTITY' : 'FORMULARE_AMLZ_C3A_SELECT'}
                defaultValue={showTable || undefined}
                onChange={value => handleSelectChange('ma', value)}
                data={anoNeSelect}
            />
            {showTable === EAnoNe.ANO && (
                <TableComponent
                    locale={{
                        emptyText: <TranslateText>ZADNE_UDAJE</TranslateText>
                    }}
                    style={{
                        margin: '30px 0px'
                    }}
                    rowKey={(_, index) => index || 0}
                    columns={columns}
                    dataSource={C3dataPrima?.subj}
                    loading={!C3dataPrima}
                    pagination={false}
                    footer={() => (
                        <AnchorComponent
                            id="c3PridatOsobu"
                            style={{
                                textAlign: 'center',
                                display: 'block'
                            }}
                            onClick={() => {
                                setPersonData({});
                                setSubjIndex(-1);
                            }}
                        >
                            <TranslateText>PRIDAT_OSOBU</TranslateText>
                        </AnchorComponent>
                    )}
                />
            )}
            {percentaVlastnictva > 100 && (
                <Paragraph text={'NEVALIDNI_PROCENTA_CELKEM'} className="extramessage-text" />
            )}
            {isC6edit ? (
                <Row className="button-row">
                    <ButtonComponent
                        buttontext="POKRACOVAT_NA_ZHRNUTI"
                        id="C3PrehledBtn"
                        type="primary"
                        onClick={() => form.submit()}
                        disabled={percentaVlastnictva > 100 || (!C3dataPrima?.subj?.length && showTable === EAnoNe.ANO)}
                    />
                </Row>
            ) : (
                <Row className="button-row">
                    <ButtonComponent buttontext="ZPET" onClick={() => prevStep()} id="C3ZpetBtn" type="default" />
                    <ButtonComponent
                        buttontext="POKRACOVAT"
                        id="C3PokracovatBtn"
                        type="primary"
                        onClick={() => form.submit()}
                        disabled={percentaVlastnictva > 100 || (!C3dataPrima?.subj?.length && showTable === EAnoNe.ANO)}
                    />
                </Row>
            )}
        </FormWrapper>
    ) : (
        <C3PridatOsobu
            mode={'C3A'}
            setSubmitPersonData={setSubmitPersonData}
            personData={personData}
            setPersonData={setPersonData}
            countries={countries}
        />
    );
};

export default C3;
